import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useProjectsAnalyticsL1 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { ProjectActions } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-common/src/ui/project-actions/ProjectActions.tsx';
import type { JiraLinkConfig } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import { useProjectMenuItemDragAndDrop } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { ContainerAvatar as Avatar } from '@atlaskit/side-nav-items/container-avatar';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { JswNonExpandableProject$key } from './__generated__/JswNonExpandableProject.graphql';

type JswNonExpandableProjectProp = {
	projectData: JswNonExpandableProject$key;
	menuItemAnchorRef?: React.RefObject<HTMLAnchorElement>;
};

export const JswNonExpandableProject = ({
	projectData,
	menuItemAnchorRef,
}: JswNonExpandableProjectProp): React.JSX.Element => {
	const { sendProjectMenuItemAnalyticsEvent } = useProjectsAnalyticsL1();
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<JswNonExpandableProject$key>(
		graphql`
			fragment JswNonExpandableProject on JiraProject {
				projectId @required(action: THROW)
				projectKey: key @required(action: THROW)
				isFavourite
				projectType
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				defaultNavigationItem {
					... on JiraNavigationItem {
						url
					}
				}
				...ProjectActions
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		projectData,
	);

	const routerLinkConfig: JiraLinkConfig = useMemo(
		() => ({
			to: project.defaultNavigationItem?.url ?? `/browse/${project.projectKey}`,
			prefetch: 'hover',
		}),
		[project.defaultNavigationItem?.url, project.projectKey],
	);

	const { projectId, projectKey, isFavourite, name, avatar } = project;
	const { state, draggableAnchorRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId,
			projectKey,
			isFavourite,
			name,
			avatar,
			isEnabled: !!isFavourite,
		});

	return (
		<>
			<Nav4MenuLinkItem<JiraLinkConfig>
				ref={mergeRefs(menuItemAnchorRef || null, draggableAnchorRef || null)}
				visualContentRef={dropTargetRef}
				dropIndicator={dropIndicator}
				isDragging={state.type === 'dragging'}
				hasDragIndicator={hasDragIndicator}
				elemBefore={<Avatar src={project.avatar.large || ''} />}
				href={routerLinkConfig}
				// oxlint-disable-next-line react/react-compiler
				menuId={ENTITY_ID.PROJECT(project.projectKey)}
				onClick={useCallback(() => {
					sendProjectMenuItemAnalyticsEvent({
						starred: project.isFavourite ?? false,
						recent: !project.isFavourite,
						projectId: project.projectId,
						projectType: project.projectType,
					});
				}, [
					project.isFavourite,
					project.projectId,
					project.projectType,
					sendProjectMenuItemAnalyticsEvent,
				])}
				actionsOnHover={<ProjectActions projectFragment={project} />}
			>
				{project.name}
			</Nav4MenuLinkItem>
			{dragPreview}
		</>
	);
};
